import React, { useState, useMemo } from 'react';
import {
  Search,
  User,
  Calendar,
  Mail,
  Phone,
  MapPin,
  FileText,
  X
} from 'lucide-react';
import useFetchData from 'hooks/custom/useFetchData';
import apiEndpoints from 'constants/apiEndpoints';
import { PrintFormButton } from './hr-forms/PrintFormButton';

// Mock data
// const employees = [
//   {
//     id: 1,
//     name: 'John Smith',
//     email: 'john.smith@company.com',
//     phone: '+1 (555) 123-4567',
//     department: 'Engineering',
//     position: 'Senior Developer',
//     location: 'New York, NY',
//     joinDate: '2020-03-15',
//     avatar: 'JS'
//   },
//   {
//     id: 2,
//     name: 'Sarah Johnson',
//     email: 'sarah.johnson@company.com',
//     phone: '+1 (555) 234-5678',
//     department: 'Marketing',
//     position: 'Marketing Manager',
//     location: 'Los Angeles, CA',
//     joinDate: '2019-07-22',
//     avatar: 'SJ'
//   },
//   {
//     id: 3,
//     name: 'Michael Brown',
//     email: 'michael.brown@company.com',
//     phone: '+1 (555) 345-6789',
//     department: 'HR',
//     position: 'HR Specialist',
//     location: 'Chicago, IL',
//     joinDate: '2021-01-10',
//     avatar: 'MB'
//   },
//   {
//     id: 4,
//     name: 'Emily Davis',
//     email: 'emily.davis@company.com',
//     phone: '+1 (555) 456-7890',
//     department: 'Finance',
//     position: 'Financial Analyst',
//     location: 'Boston, MA',
//     joinDate: '2020-09-05',
//     avatar: 'ED'
//   },
//   {
//     id: 5,
//     name: 'Robert Wilson',
//     email: 'robert.wilson@company.com',
//     phone: '+1 (555) 567-8901',
//     department: 'Engineering',
//     position: 'DevOps Engineer',
//     location: 'Seattle, WA',
//     joinDate: '2021-11-20',
//     avatar: 'RW'
//   }
// ];

const formTypes = [
  { id: 'hrDeclarationForm', name: 'HR Declaration Form' },
  { id: 'declarationFormEPFO', name: 'Declaration Form (EPFO)' },
  { id: 'declarationFormESIC', name: 'Declaration Form (ESIC)' },
  { id: 'employmentCard', name: 'Employment Card (Form X)' },
  {
    id: 'nominationForm',
    name: 'Nomination and Declaration Form'
  }
];

const HRReports = () => {
  const [employees] = useFetchData(apiEndpoints.employee.getAll);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedFormType, setSelectedFormType] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [employeeDetail] = useFetchData(
    apiEndpoints.employee.getById(selectedEmployee?.employee_id),
    {
      dependency: [selectedEmployee],
      when: () => selectedEmployee?.employee_id
    }
  );
  console.log('🚀 ~ HRReports ~ employeeDetail:', employeeDetail);

  const filteredEmployees = useMemo(() => {
    return employees.filter(
      employee =>
        (employee.first_name + ' ' + employee.last_name)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (employee.employee_email || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        `${employee.employee_mobile || ''}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (employee.department_name || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (employee.designation_name || '')
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, employees]);

  const handleEmployeeSelect = employee => {
    setSelectedEmployee(employee);
    setShowSearchModal(false);
    setSearchTerm('');
  };
  const formRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 200px',
    gap: '1.5rem',
    alignItems: 'end'
  };

  const fieldGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  };

  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151'
  };

  const selectInputStyle = {
    padding: '0.75rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '0.875rem',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const bottomCardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0'
  };

  const employeeInfoStyle = {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '1.5rem',
    alignItems: 'start'
  };

  const avatarStyle = {
    width: '80px',
    height: '80px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '1.5rem',
    fontWeight: '600'
  };

  const employeeDetailsStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem'
  };

  const detailItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    background: '#f8fafc',
    borderRadius: '8px'
  };

  const detailIconStyle = {
    color: '#6b7280',
    flexShrink: 0
  };

  const detailTextStyle = {
    fontSize: '0.875rem',
    color: '#374151'
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1020,
    padding: '1rem'
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflow: 'hidden',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
  };

  const modalHeaderStyle = {
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const modalTitleStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1f2937',
    margin: 0
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '6px',
    color: '#6b7280',
    transition: 'all 0.2s ease'
  };

  const searchInputStyle = {
    width: '100%',
    padding: '0.75rem 1rem 0.75rem 2.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '0.875rem',
    outline: 'none'
  };

  const searchContainerStyle = {
    position: 'relative',
    margin: '1rem 1.5rem'
  };

  const searchIconStyle = {
    position: 'absolute',
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af'
  };

  const employeeListStyle = {
    maxHeight: '400px',
    overflowY: 'auto',
    padding: '0 1.5rem 1.5rem'
  };

  const employeeCardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'white'
  };

  const compactAvatarStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '600',
    flexShrink: 0
  };

  const compactDetailsStyle = {
    flex: 1,
    minWidth: 0
  };

  const compactNameStyle = {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#1f2937',
    margin: '0 0 0.25rem 0'
  };

  const compactInfoStyle = {
    fontSize: '0.75rem',
    color: '#6b7280',
    margin: 0
  };

  const compactBadgeStyle = {
    background: '#eff6ff',
    color: '#2563eb',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  };

  const emptyStateStyle = {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#9ca3af'
  };

  // Responsive adjustments
  const isSmallScreen = window.innerWidth <= 768;

  const responsiveFormRowStyle = {
    ...formRowStyle,
    gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr 200px',
    gap: isSmallScreen ? '1rem' : '1.5rem'
  };

  const responsiveEmployeeDetailsStyle = {
    ...employeeDetailsStyle,
    gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr'
  };

  return (
    <div>
      {/* Header */}
      <div className="gap-2 d-flex  justify-content-between flex-column flex-sm-row ">
        <div>
          <h2 className="mb-5 font-outfit fw-medium">HR Reports</h2>
        </div>
      </div>

      {/* Top Card - Selection Form */}
      <div className="card card-body mb-4">
        <div style={responsiveFormRowStyle}>
          {/* Employee Selection */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Select Employee</label>
            <div
              style={selectInputStyle}
              onClick={() => setShowSearchModal(true)}
              onMouseEnter={e => (e.target.style.borderColor = '#3b82f6')}
              onMouseLeave={e => (e.target.style.borderColor = '#d1d5db')}
            >
              <span style={{ color: selectedEmployee ? '#1f2937' : '#9ca3af' }}>
                {selectedEmployee
                  ? selectedEmployee.first_name +
                    ' ' +
                    selectedEmployee.last_name
                  : 'Choose an employee...'}
              </span>
              <User size={16} style={{ color: '#9ca3af' }} />
            </div>
          </div>
          {/* Form Type Selection */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Form Type</label>
            <select
              value={selectedFormType}
              onChange={e => setSelectedFormType(e.target.value)}
              style={{
                ...selectInputStyle,
                cursor: 'pointer'
              }}
              onFocus={e => (e.target.style.borderColor = '#3b82f6')}
              onBlur={e => (e.target.style.borderColor = '#d1d5db')}
            >
              <option value="">Select form type...</option>
              {formTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          {/* Download Button */}
          <PrintFormButton
            buttonProps={{ disabled: !selectedEmployee || !selectedFormType }}
            formType={selectedFormType}
            data={employeeDetail}
          />
        </div>
      </div>

      {/* Bottom Card - Employee Details */}
      {selectedEmployee ? (
        <div style={bottomCardStyle}>
          <div style={employeeInfoStyle}>
            <div style={avatarStyle}>{selectedEmployee.avatar}</div>
            <div>
              <h3
                style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: '#1f2937'
                }}
              >
                {selectedEmployee.first_name} {selectedEmployee.last_name}
              </h3>
              <div style={responsiveEmployeeDetailsStyle}>
                <div style={detailItemStyle}>
                  <Mail size={16} style={detailIconStyle} />
                  <span style={detailTextStyle}>
                    {selectedEmployee.employee_email}
                  </span>
                </div>
                <div style={detailItemStyle}>
                  <Phone size={16} style={detailIconStyle} />
                  <span style={detailTextStyle}>
                    {selectedEmployee.employee_mobile}
                  </span>
                </div>
                <div style={detailItemStyle}>
                  <User size={16} style={detailIconStyle} />
                  <span style={detailTextStyle}>
                    {selectedEmployee.designation_name}
                  </span>
                </div>
                <div style={detailItemStyle}>
                  <MapPin size={16} style={detailIconStyle} />
                  <span style={detailTextStyle}>
                    {selectedEmployee.loc_area_name}
                  </span>
                </div>
                <div style={detailItemStyle}>
                  <Calendar size={16} style={detailIconStyle} />
                  <span style={detailTextStyle}>
                    Joined:{' '}
                    {new Date(
                      selectedEmployee.date_of_joining
                    ).toLocaleDateString()}
                  </span>
                </div>
                <div style={detailItemStyle}>
                  <FileText size={16} style={detailIconStyle} />
                  <span style={detailTextStyle}>
                    {selectedEmployee.department_name}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={bottomCardStyle}>
          <div style={emptyStateStyle}>
            <User size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Select an employee to view their details
            </p>
          </div>
        </div>
      )}

      {/* Search Modal */}
      {showSearchModal && (
        <div
          style={modalOverlayStyle}
          onClick={e => {
            if (e.target === e.currentTarget) {
              setShowSearchModal(false);
              setSearchTerm('');
            }
          }}
        >
          <div style={modalStyle}>
            <div style={modalHeaderStyle}>
              <h3 style={modalTitleStyle}>Select Employee</h3>
              <button
                onClick={() => {
                  setShowSearchModal(false);
                  setSearchTerm('');
                }}
                style={closeButtonStyle}
                onMouseEnter={e => (e.target.style.background = '#f3f4f6')}
                onMouseLeave={e => (e.target.style.background = 'none')}
              >
                <X size={20} />
              </button>
            </div>

            <div style={searchContainerStyle}>
              <Search size={16} style={searchIconStyle} />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={searchInputStyle}
                autoFocus
                onFocus={e => (e.target.style.borderColor = '#3b82f6')}
                onBlur={e => (e.target.style.borderColor = '#d1d5db')}
              />
            </div>

            <div style={employeeListStyle}>
              {filteredEmployees.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: '#9ca3af'
                  }}
                >
                  No employees found
                </div>
              ) : (
                filteredEmployees.map(employee => (
                  <div
                    key={employee.id}
                    style={employeeCardStyle}
                    onClick={() => handleEmployeeSelect(employee)}
                    onMouseEnter={e => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.background = '#f8fafc';
                    }}
                    onMouseLeave={e => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.background = 'white';
                    }}
                  >
                    <div style={compactAvatarStyle}>{employee.avatar}</div>
                    <div style={compactDetailsStyle}>
                      <h4 style={compactNameStyle}>
                        {employee.first_name} {employee.last_name}
                      </h4>
                      <p style={compactInfoStyle}>
                        {employee.designation_name} • {employee.employee_mobile}
                      </p>
                    </div>
                    <div style={compactBadgeStyle}>
                      {employee.department_name}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRReports;
